/**
 * Targeting V2 bulk-bid currency rules (min bid + decimal scale).
 * Currency from clientData → amazon_setup.profile_info.currencyCode.
 */

export const TARGETING_V2_DEFAULT_CURRENCY_CODE = "INR";

/**
 * Minimum bid + decimal scale by Amazon marketplace currency.
 * scale: 0 = whole numbers, 2 = two decimal places.
 */
export const TARGETING_V2_CURRENCY_BID_RULES = {
  MXN: { minBid: 0.1, scale: 2 },
  GBP: { minBid: 0.02, scale: 2 },
  EUR: { minBid: 0.02, scale: 2 },
  CAD: { minBid: 0.02, scale: 2 },
  AUD: { minBid: 0.02, scale: 2 },
  USD: { minBid: 0.02, scale: 2 },
  JPY: { minBid: 2, scale: 0 },
  AED: { minBid: 0.1, scale: 2 },
  BRL: { minBid: 0.07, scale: 2 },
  SGD: { minBid: 0.02, scale: 2 },
  SEK: { minBid: 0.18, scale: 2 },
  INR: { minBid: 1, scale: 2 },
  PLN: { minBid: 0.04, scale: 2 },
  TRY: { minBid: 0.05, scale: 2 },
  EGP: { minBid: 0.15, scale: 2 },
  SAR: { minBid: 0.1, scale: 2 },
  ZAR: { minBid: 1, scale: 0 },
};

export function getAmazonCurrencyCodeFromClientData() {
  try {
    const clientData = JSON.parse(localStorage.getItem("clientData") || "{}");
    const code = clientData?.amazon_setup?.profile_info?.currencyCode;
    if (typeof code === "string" && code.trim()) {
      return code.trim().toUpperCase();
    }
  } catch {
    // fall through
  }
  return TARGETING_V2_DEFAULT_CURRENCY_CODE;
}

export function resolveTargetingCurrencyCode(currencyCode) {
  const code = String(currencyCode || TARGETING_V2_DEFAULT_CURRENCY_CODE)
    .trim()
    .toUpperCase();
  if (
    Object.prototype.hasOwnProperty.call(TARGETING_V2_CURRENCY_BID_RULES, code)
  ) {
    return code;
  }
  return TARGETING_V2_DEFAULT_CURRENCY_CODE;
}

export function getTargetingBidRule(currencyCode) {
  const resolved = resolveTargetingCurrencyCode(currencyCode);
  return TARGETING_V2_CURRENCY_BID_RULES[resolved];
}

export function getTargetingFractionDigits(currencyCode) {
  return getTargetingBidRule(currencyCode).scale;
}

export function getTargetingMinBid(currencyCode) {
  return getTargetingBidRule(currencyCode).minBid;
}

export function roundTargetingBid(value, currencyCode) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const fractionDigits = getTargetingFractionDigits(currencyCode);
  if (fractionDigits === 0) return Math.round(numeric);
  const factor = 10 ** fractionDigits;
  return Math.round(numeric * factor) / factor;
}

/**
 * Rules object for BulkAdjustBidModalV2 (Targeting V2 only).
 */
export function getTargetingV2BidRules(
  currencyCode = getAmazonCurrencyCodeFromClientData()
) {
  const resolved = resolveTargetingCurrencyCode(currencyCode);
  const { minBid, scale } = TARGETING_V2_CURRENCY_BID_RULES[resolved];
  return {
    currencyCode: resolved,
    unit: resolved,
    minBid,
    fractionDigits: scale,
    roundBid: (value) => roundTargetingBid(value, resolved),
  };
}
